import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock, toDisplayString as _toDisplayString, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-79e96f0b"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["id", "x1", "y1", "x2", "y2"]
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("stop", {
  offset: "1%",
  style: {"stop-color":"rgb(229, 161, 172)"}
}, null, -1))
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("stop", {
  offset: "100%",
  style: {"stop-color":"rgb(80, 34, 166)"}
}, null, -1))
const _hoisted_4 = [
  _hoisted_2,
  _hoisted_3
]
const _hoisted_5 = ["id", "x1", "y1", "x2", "y2"]
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("stop", {
  offset: "0%",
  style: {"stop-color":"#46a1f1"}
}, null, -1))
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("stop", {
  offset: "100%",
  style: {"stop-color":"#46a1f1"}
}, null, -1))
const _hoisted_8 = [
  _hoisted_6,
  _hoisted_7
]
const _hoisted_9 = ["id", "markerWidth", "markerHeight"]
const _hoisted_10 = { class: "conditionLabel relative" }
const _hoisted_11 = { class: "scrollable-tooltip-content overflow-auto" }

import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  getStraightPath,
  Position,
} from "@vue-flow/core";
import { computed, ref, onMounted, nextTick, watch } from "vue";
import { ElTooltip } from "element-plus";
import { getLoopInfo, isNodeDetachedFromFlow } from "@/flow/utils/nodeUtils";
import { isStepSkipped } from "@/flow/utils/skipUtils";
import { checkIsLoop } from "@/core/util";
import NodeFilterSummary from "@/flow/components/vue-flow/NodeFilterSummary.vue";
import {
  DEFAULT_MARKER_COLOR,
  DETACHED_MARKER_COLOR,
  SELECTED_MARKER_COLOR,
} from "@/flow/utils/loopEdgeUtils";

/**
 * Keeps the filter card off the flow it describes. The funnel sits on the edge itself, so the
 * clear space is above it; Popper flips below, then sideways, when the canvas edge intrudes.
 */
export interface IProps {
  id: string;
  edgeType?: "router" | "node";
  sourceX: number;
  edgeClass?: "customRouterEdge" | "customNodeEdge";
  sourceY: number;
  targetX: number;
  data: any;
  targetY: number;
  isAnimated?: boolean;
  markerEnd?: unknown;
  sourcePosition: Position;
  targetPosition: Position;
  selectedEdge: any;
  parrentIndex: any;
  nodes?: any[];
  edges?: any[];
  stepList?: any[];
  nodesDraggable: boolean;
  router?: any;
  isLoopEdge: boolean;
  targetStepData?: any;
  /** Turns on the hover card describing the source step's filter. Wizard preview only. */
  showInputSummary?: boolean;
}
const HANDLE_EXTENSION = 1;

const ARROW_MASK_LENGTH = 2;

const Y_AXIS_THRESHOLD = 1.5;

const labelOffsetFromTarget = 50;
const filterPositionRatio = 0.35;

export default /*#__PURE__*/_defineComponent({
  __name: 'CustomEdge',
  props: {
    id: {},
    edgeType: {},
    sourceX: {},
    edgeClass: {},
    sourceY: {},
    targetX: {},
    data: {},
    targetY: {},
    isAnimated: { type: Boolean },
    markerEnd: {},
    sourcePosition: {},
    targetPosition: {},
    selectedEdge: {},
    parrentIndex: {},
    nodes: {},
    edges: {},
    stepList: {},
    nodesDraggable: { type: Boolean },
    router: {},
    isLoopEdge: { type: Boolean },
    targetStepData: {},
    showInputSummary: { type: Boolean }
  },
  emits: ["remove-router", "remove-edge", "open-filter", "remove-filter"],
  setup(__props: any, { emit: __emit }) {

const BADGE_POPPER_OPTIONS = {
  modifiers: [
    {
      name: "flip",
      options: { fallbackPlacements: ["bottom", "right", "left"] },
    },
    { name: "preventOverflow", options: { padding: 8 } },
  ],
};

const props = __props;
const emit = __emit;

const TARGET_HANDLE_OFFSETS: Record<Position, { x: number; y: number }> = {
  [Position.Left]: { x: -HANDLE_EXTENSION, y: 0 },
  [Position.Right]: { x: HANDLE_EXTENSION, y: 0 },
  [Position.Top]: { x: 0, y: 4 },
  [Position.Bottom]: { x: 0, y: HANDLE_EXTENSION },
};

const getTargetHandleOffset = (position: Position) =>
  TARGET_HANDLE_OFFSETS[position] ?? { x: 0, y: 0 };

const edgeStrokeDasharray = ref<string | null>(null);

const updateEdgeStrokeDash = (pathEl: SVGPathElement) => {
  const totalLength = pathEl.getTotalLength();
  if (!Number.isFinite(totalLength) || totalLength <= 0) {
    edgeStrokeDasharray.value = null;
    return;
  }

  const gap = Math.min(totalLength / 2, ARROW_MASK_LENGTH);
  const visible = Math.max(totalLength - gap, 0);
  if (gap <= 0 || visible <= 0) {
    edgeStrokeDasharray.value = null;
    return;
  }

  edgeStrokeDasharray.value = `${visible} ${gap}`;
};

const edgeGeometry = computed(() => {
  const { sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition } =
    props;
  const targetOffset = getTargetHandleOffset(targetPosition);

  return {
    sourceX,
    sourceY,
    targetX: targetX + targetOffset.x,
    targetY: targetY + targetOffset.y,
    sourcePosition,
    targetPosition,
  };
});

const gradientX1 = computed(() => edgeGeometry.value.sourceX);
const gradientY1 = computed(() => edgeGeometry.value.sourceY);
const gradientX2 = computed(() => edgeGeometry.value.targetX);
const gradientY2 = computed(() => edgeGeometry.value.targetY);

const path = computed(() => {
  const {
    sourceX,
    sourceY,
    targetX: finalTargetX,
    targetY: finalTargetY,
    sourcePosition,
    targetPosition,
  } = edgeGeometry.value;

  // Safety check - if coordinates are invalid, fallback to smooth step path
  if (
    !Number.isFinite(sourceX) ||
    !Number.isFinite(sourceY) ||
    !Number.isFinite(finalTargetX) ||
    !Number.isFinite(finalTargetY)
  ) {
    return getSmoothStepPath({
      sourceX,
      sourceY,
      targetX: finalTargetX,
      targetY: finalTargetY,
      sourcePosition,
      targetPosition,
    });
  }

  const yDiff = Math.abs(sourceY - finalTargetY);
  const isHorizontalEdge =
    sourcePosition === Position.Right && targetPosition === Position.Left;

  // If Y difference is within threshold and it's a horizontal edge, use straight line
  if (isHorizontalEdge && yDiff <= Y_AXIS_THRESHOLD) {
    return getStraightPath({
      sourceX,
      sourceY,
      targetX: finalTargetX,
      targetY: finalTargetY,
    });
  }

  return getSmoothStepPath({
    sourceX,
    sourceY,
    targetX: finalTargetX,
    targetY: finalTargetY,
    sourcePosition,
    targetPosition,
  });
});

const isEdgeSelected = computed(
  () => props.selectedEdge && props.selectedEdge.id === props.id
);

const isLoopBackEdge = computed(() => {
  return props.isLoopEdge;
});

const currentEdge = computed(() => {
  if (!Array.isArray(props.edges)) return null;
  return props.edges.find((e: any) => e?.id === props.id) ?? null;
});

const currentEdgeMarkerColor = computed(() => {
  const markerEnd = currentEdge.value?.markerEnd;
  if (!markerEnd || typeof markerEnd !== "object") return "";
  return String((markerEnd as any)?.color ?? "").trim();
});

const normalizedCurrentEdgeMarkerColor = computed(() =>
  currentEdgeMarkerColor.value.toLowerCase()
);

const visualEdgeColor = computed(() => {
  if (isEdgeSelected.value) return SELECTED_MARKER_COLOR;
  if (isDetachedEdge.value) return DETACHED_MARKER_COLOR;
  if (isSkippedEdge.value) return DETACHED_MARKER_COLOR;
  if (isInvalidLoopBackEdge.value) return "red";
  return "";
});

const markerEndIndicatesInvalid = computed(() => {
  const markerColor = normalizedCurrentEdgeMarkerColor.value;
  if (markerColor === "red" || markerColor === "#ff0000") return true;

  const markerEnd = String(props.markerEnd || "").toLowerCase();
  return (
    markerEnd.includes("color=red") ||
    markerEnd.includes("color=#ff0000") ||
    markerEnd.includes("color=%23ff0000")
  );
});

const isInvalidLoopBackEdge = computed(() => {
  if (markerEndIndicatesInvalid.value) return true;
  if (!isLoopBackEdge.value) return false;

  return !!(
    props.data?.invalidLoopBackConnection ||
    props.targetStepData?.invalidLoopBackConnection
  );
});

const isDetachedEdge = computed(() => {
  return (
    isNodeDetachedFromFlow(props.data) &&
    isNodeDetachedFromFlow(props.targetStepData)
  );
});

const isSkippedEdge = computed(() => {
  return isStepSkipped(props.data) || isStepSkipped(props.targetStepData);
});

const visualMarkerColor = computed(() => {
  return (
    visualEdgeColor.value ||
    currentEdgeMarkerColor.value ||
    DEFAULT_MARKER_COLOR
  );
});

const visualMarkerWidth = computed(() => {
  const markerWidth = (currentEdge.value?.markerEnd as any)?.width;
  if (typeof markerWidth === "number") {
    return markerWidth;
  }
  return 7;
});

const toMarkerToken = (value: string | number) =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");

const toStableSvgIdToken = (value: string | number) =>
  Array.from(String(value))
    .map((character) =>
      (character.codePointAt(0) ?? 0).toString(16).padStart(6, "0")
    )
    .join("");

const edgeGradientId = computed(() =>
  ["custom-edge-gradient", toStableSvgIdToken(props.id)].join("-")
);

const highlightedEdgeGradientId = computed(() =>
  ["custom-edge-highlighted-gradient", toStableSvgIdToken(props.id)].join("-")
);

const visualMarkerId = computed(() => {
  return [
    "custom-edge-marker",
    toStableSvgIdToken(props.id),
    toMarkerToken(visualMarkerColor.value),
    toMarkerToken(visualMarkerWidth.value),
  ].join("-");
});

const visualMarkerEnd = computed(() => {
  if (visualMarkerWidth.value <= 0) return undefined;
  return `url(#${visualMarkerId.value})`;
});

const baseEdgeStyle = computed(() => {
  const strokeStyle: Record<string, string | undefined> = {
    animation: "none",
    strokeWidth: props.isAnimated ? "5.5 !important" : "4.5 !important",
    stroke: visualEdgeColor.value || `url(#${edgeGradientId.value})`,
    strokeLinecap: "butt",
  };

  if (
    !props.isAnimated &&
    edgeStrokeDasharray.value &&
    props.targetPosition !== Position.Top
  ) {
    strokeStyle.strokeDasharray = edgeStrokeDasharray.value;
  }

  return strokeStyle;
});

const canRemoveRouterCondition = computed(() => {
  if (!currentRouterStep.value || !props.nodes || !props.edges) {
    return true;
  }

  const routerNodeId = currentRouterStep.value.data?.origId;
  if (!routerNodeId) {
    return true;
  }

  // Check if the router is inside a loop with only 1 step
  // This is to maintain have at least one step inside loop
  const loopInfo = getLoopInfo(routerNodeId, props.nodes, props.edges);
  if (loopInfo.isInLoop && loopInfo.stepCount <= 1) {
    // If router has multiple routes/conditions, allow deletion until only one remains
    const routerRoutes = currentRouterStep.value.data?.routes;
    if (routerRoutes && routerRoutes.length > 1) {
      return true;
    }
    return false;
  }

  return true;
});

const fallbackLabelX = computed(() => props.targetX);
const fallbackLabelY = computed(() => props.targetY - labelOffsetFromTarget);
const labelPositionRatio = computed(() => {
  const dx = props.targetX - props.sourceX;
  const dy = props.targetY - props.sourceY;
  const distance = Math.hypot(dx, dy);
  if (!Number.isFinite(distance) || distance <= 0) {
    return 0.7;
  }

  const ratioFromTarget = Math.min(labelOffsetFromTarget / distance, 0.9);
  const ratio = 1 - ratioFromTarget;
  return Math.min(Math.max(ratio, 0.05), 0.95);
});
const labelPoint = ref({ x: fallbackLabelX.value, y: fallbackLabelY.value });
const fallbackFilterX = computed(
  () => props.sourceX + (props.targetX - props.sourceX) * filterPositionRatio
);
const fallbackFilterY = computed(
  () => props.sourceY + (props.targetY - props.sourceY) * filterPositionRatio
);
const filterPoint = ref({ x: fallbackFilterX.value, y: fallbackFilterY.value });
const filterPathRef = ref<SVGPathElement | null>(null);

const setFallbackFilterPoint = () => {
  filterPoint.value = { x: fallbackFilterX.value, y: fallbackFilterY.value };
};

const setFallbackLabelPoint = () => {
  labelPoint.value = { x: fallbackLabelX.value, y: fallbackLabelY.value };
};

const updateFilterPoint = () => {
  const pathData = path.value?.[0];
  if (!pathData || typeof document === "undefined") {
    setFallbackFilterPoint();
    return;
  }

  try {
    const pathEl =
      filterPathRef.value ||
      document.createElementNS("http://www.w3.org/2000/svg", "path");
    pathEl.setAttribute("d", pathData);
    updateEdgeStrokeDash(pathEl);
    filterPathRef.value = pathEl;

    const totalLength = pathEl.getTotalLength();
    if (!Number.isFinite(totalLength) || totalLength <= 0) {
      setFallbackFilterPoint();
      return;
    }

    const point = pathEl.getPointAtLength(totalLength * filterPositionRatio);
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      setFallbackFilterPoint();
      return;
    }

    filterPoint.value = { x: point.x, y: point.y };
  } catch {
    setFallbackFilterPoint();
  }
};

const updateLabelPoint = () => {
  const pathData = path.value?.[0];
  if (!pathData || typeof document === "undefined") {
    setFallbackLabelPoint();
    return;
  }

  try {
    const pathEl =
      filterPathRef.value ||
      document.createElementNS("http://www.w3.org/2000/svg", "path");
    pathEl.setAttribute("d", pathData);
    updateEdgeStrokeDash(pathEl);
    filterPathRef.value = pathEl;

    const totalLength = pathEl.getTotalLength();
    if (!Number.isFinite(totalLength) || totalLength <= 0) {
      setFallbackLabelPoint();
      return;
    }

    const point = pathEl.getPointAtLength(
      totalLength * labelPositionRatio.value
    );
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      setFallbackLabelPoint();
      return;
    }

    labelPoint.value = { x: point.x, y: point.y };
  } catch {
    setFallbackLabelPoint();
  }
};

watch(
  () => path.value?.[0],
  () => {
    updateFilterPoint();
    updateLabelPoint();
  },
  { immediate: true }
);

const filterX = computed(() =>
  Number.isFinite(filterPoint.value.x)
    ? filterPoint.value.x
    : fallbackFilterX.value
);
const filterY = computed(() =>
  Number.isFinite(filterPoint.value.y)
    ? filterPoint.value.y
    : fallbackFilterY.value
);
const labelX = computed(() =>
  Number.isFinite(labelPoint.value.x)
    ? labelPoint.value.x
    : fallbackLabelX.value
);
const labelY = computed(() =>
  Number.isFinite(labelPoint.value.y)
    ? labelPoint.value.y
    : fallbackLabelY.value
);
const currentRouterStep = computed(() => {
  const step = props.nodes.find(
    (node) => node.data.id === props.router.route.parentId
  );
  return step;
});

const labelTextRef = ref<HTMLElement | null>(null);
const showTooltip = ref(false);

const updateTooltipVisibility = () => {
  const el = labelTextRef.value;
  if (!el) {
    showTooltip.value = false;
    return;
  }

  const hasVerticalOverflow = el.scrollHeight > el.clientHeight + 1;
  const hasHorizontalOverflow = el.scrollWidth > el.clientWidth + 1;

  showTooltip.value = hasVerticalOverflow || hasHorizontalOverflow;
};

onMounted(() => {
  nextTick(updateTooltipVisibility);
});

watch(
  () => props.router?.route?.name,
  () => {
    nextTick(updateTooltipVisibility);
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(BaseEdge), {
      class: _normalizeClass(_ctx.edgeClass),
      style: _normalizeStyle(baseEdgeStyle.value),
      id: _ctx.id,
      path: path.value[0],
      "marker-end": visualMarkerEnd.value
    }, null, 8, ["class", "style", "id", "path", "marker-end"]),
    (_openBlock(), _createElementBlock("svg", null, [
      _createElementVNode("defs", null, [
        _createElementVNode("linearGradient", {
          id: edgeGradientId.value,
          gradientUnits: "userSpaceOnUse",
          x1: gradientX1.value,
          y1: gradientY1.value,
          x2: gradientX2.value,
          y2: gradientY2.value
        }, _hoisted_4, 8, _hoisted_1),
        _createElementVNode("linearGradient", {
          id: highlightedEdgeGradientId.value,
          gradientUnits: "userSpaceOnUse",
          x1: gradientX1.value,
          y1: gradientY1.value,
          x2: gradientX2.value,
          y2: gradientY2.value
        }, _hoisted_8, 8, _hoisted_5),
        (visualMarkerWidth.value > 0)
          ? (_openBlock(), _createElementBlock("marker", {
              key: 0,
              id: visualMarkerId.value,
              viewBox: "-10 -10 20 20",
              refX: "0",
              refY: "0",
              markerWidth: visualMarkerWidth.value,
              markerHeight: visualMarkerWidth.value,
              markerUnits: "strokeWidth",
              orient: "auto"
            }, [
              _createElementVNode("polyline", {
                style: _normalizeStyle({
            stroke: visualMarkerColor.value,
            fill: visualMarkerColor.value,
            strokeWidth: 1,
          }),
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                points: "-5,-4 0,0 -5,4 -5,-4"
              }, null, 4)
            ], 8, _hoisted_9))
          : _createCommentVNode("", true)
      ])
    ])),
    _createVNode(_unref(EdgeLabelRenderer), { class: "relative" }, {
      default: _withCtx(() => [
        _createElementVNode("div", {
          style: _normalizeStyle({
        pointerEvents: 'all',
        position: 'absolute',
        transform: `translate(-50%, -50%) translate(${filterX.value}px, ${filterY.value}px)`,
      }),
          class: "nodrag nopan"
        }, [
          (
          _ctx.data.type !== 'ROUTER' &&
          !_unref(checkIsLoop)(_ctx.data) &&
          _ctx.data.type !== 'LOOP_END' &&
          _ctx.data?.filter?.length &&
          _ctx.sourcePosition === 'right'
        )
            ? (_openBlock(), _createBlock(_unref(ElTooltip), {
                key: 0,
                effect: "custom",
                placement: "top",
                "show-after": 250,
                disabled: !_ctx.showInputSummary,
                "popper-options": BADGE_POPPER_OPTIONS
              }, {
                content: _withCtx(() => [
                  _createVNode(NodeFilterSummary, { step: _ctx.data }, null, 8, ["step"])
                ]),
                default: _withCtx(() => [
                  _createElementVNode("button", {
                    onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (_ctx.nodesDraggable && emit('open-filter', _ctx.data.ix, _ctx.data)), ["stop"])),
                    class: _normalizeClass([
            'filter-button flex items-center justify-center relative',
            { 'detached-edge-control': isDetachedEdge.value },
          ])
                  }, [
                    (_ctx.nodesDraggable)
                      ? (_openBlock(), _createElementBlock("span", {
                          key: 0,
                          onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (emit('remove-filter', _ctx.data.id)), ["stop"])),
                          class: "close-btn"
                        }, [
                          _createVNode(_component_font_awesome_icon, {
                            class: "icon",
                            icon: "times",
                            size: "sm"
                          })
                        ]))
                      : _createCommentVNode("", true),
                    _createVNode(_component_font_awesome_icon, { icon: "filter" })
                  ], 2)
                ]),
                _: 1
              }, 8, ["disabled"]))
            : _createCommentVNode("", true)
        ], 4),
        (_ctx.router?.route?.name && _ctx.isAnimated && !isLoopBackEdge.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              style: _normalizeStyle({
        pointerEvents: 'all',
        position: 'absolute',
        transform: `translate(-50%, -50%) translate(${labelX.value}px, ${labelY.value}px)`,
      }),
              class: "nodrag nopan"
            }, [
              _createElementVNode("div", {
                class: _normalizeClass([
          'conditionLabelContainer flex items-center',
          { 'detached-edge-control': isDetachedEdge.value },
        ])
              }, [
                _createElementVNode("div", _hoisted_10, [
                  (showTooltip.value)
                    ? (_openBlock(), _createBlock(_unref(ElTooltip), {
                        key: 0,
                        effect: "custom",
                        placement: "auto"
                      }, {
                        content: _withCtx(() => [
                          _createElementVNode("div", _hoisted_11, _toDisplayString(_ctx.router?.route?.name), 1)
                        ]),
                        default: _withCtx(() => [
                          _createElementVNode("span", {
                            ref_key: "labelTextRef",
                            ref: labelTextRef,
                            class: "conditionLabelText"
                          }, _toDisplayString(_ctx.router?.route?.name), 513)
                        ]),
                        _: 1
                      }))
                    : (_openBlock(), _createElementBlock("span", {
                        key: 1,
                        ref_key: "labelTextRef",
                        ref: labelTextRef,
                        class: "conditionLabelText"
                      }, _toDisplayString(_ctx.router?.route?.name), 513)),
                  (_ctx.nodesDraggable && canRemoveRouterCondition.value)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 2,
                        class: "close-btn",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (
              emit('remove-router', {
                step: currentRouterStep.value?.data,
                childIndex: _ctx.router.index,
              })
            ))
                      }, [
                        _createVNode(_component_font_awesome_icon, {
                          icon: "times",
                          size: "sm"
                        })
                      ]))
                    : _createCommentVNode("", true)
                ])
              ], 2)
            ], 4))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})